import React, { useRef, useState } from 'react';
import Dialog from '../../common/components/Dialog';
import CloseButton from './modals/CloseModalButton';
import { requestPasswordReset } from '../../auth/services/AuthService';
import useClickOutside from '../hooks/useModalClickHandler';
import { useTranslations } from '../../localization/useTranslations';
import Button from '../../components/buttons/SimpleButton/Button';
import { logout } from '../../auth/services/AuthService';

interface SecurityPrivacyProps {
  userEmail: string;
  setIsSnackbarOpen: (value: boolean) => void;
  setSnackbarMessage: (message: string) => void;
}

const SecurityPrivacy: React.FC<SecurityPrivacyProps> = ({ userEmail, setIsSnackbarOpen, setSnackbarMessage }) => {
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
  const [isDeleteAccountModalOpen, setIsDeleteAccountModalOpen] = useState(false);

  const translations = useTranslations();

  const baseCrmUrl = window.crmUrl || process.env.REACT_APP_CRM_BASE_URI;

  // @TODO: SK - Check if it is working on sk site too
  const downloadDataUrl = `${baseCrmUrl}/users/users/settings?do=downloadData`;

  const passwordModalRef = useRef<HTMLDivElement>(null);
  const delAccountModalRef = useRef<HTMLDivElement>(null);

  const sendPasswordResetRequest = async (email: string) => {
    if (!email) {
      return;
    }

    try {
      await requestPasswordReset(email).then(() => {
        setIsPasswordModalOpen(false);

        setSnackbarMessage(translations.snackBarMessages.passwordResetEmailSent || 'Password reset email sent.');

        setIsSnackbarOpen(true);

        // Logout after successful password reset
        logout();
      });
    } catch (error) {
      setSnackbarMessage(
        translations.snackBarMessages.errorSendingPasswordResetEmail || 'Error sending password reset email.'
      );
      setIsSnackbarOpen(true);
    }
  };

  useClickOutside(passwordModalRef, () => setIsPasswordModalOpen(false));
  useClickOutside(delAccountModalRef, () => setIsDeleteAccountModalOpen(false));

  return (
    <div className="my-settings__section my-settings__section--security">
      {/* Security & Privacy Header */}
      <div className="my-settings__section-header">
        <h4 className="my-settings__section-title">
          {translations.myAccountSettings.securityPrivacy || 'Security & Privacy'}
        </h4>
      </div>

      {/* Buttons to trigger modals */}
      <div className="my-settings__security">
        <Button
          id="reset-password"
          onClick={() => setIsPasswordModalOpen(true)}
          className="my-settings__security-button"
          type="secondary"
          size="medium"
          label={translations.myAccountSettings.resetPassword || 'Reset Password'}
        />

        {window.country !== 'hu' && (
          <Button
            id="download-data"
            href={downloadDataUrl}
            className="my-settings__security-button"
            type="secondary"
            size="medium"
            label={translations.myAccountSettings.downloadPersonalData || 'Download Personal Data'}
          />
        )}

        <Button
          id="delete-account"
          onClick={() => setIsDeleteAccountModalOpen(true)}
          className="my-settings__security-button"
          type="secondary"
          size="medium"
          label={translations.myAccountSettings.deleteAccount || 'Delete Account'}
        />
      </div>

      {/* Modals */}
      <Dialog isOpen={isPasswordModalOpen} onClose={() => setIsPasswordModalOpen(false)}>
        <div
          className="settings-modal modal modal--large settings-modal--security settings-modal--reset-password-modal"
          ref={passwordModalRef}
        >
          <div className="modal__wrapper">
            <div className="settings-modal__header modal__header">
              <h5 className="settings-modal__title">
                {translations.myAccountSettings.resetPassword || 'Reset Password'}
              </h5>
              <CloseButton onClose={() => setIsPasswordModalOpen(false)} />
            </div>
            <div className="settings-modal__content modal__content settings-modal__content--margin">
              <p>
                {translations.myAccountSettings.weWillEmailYouAPasswordResetLink ||
                  'We will email you a password reset link.'}
              </p>
            </div>
            <div className="settings-modal__footer modal__footer">
              <button className="settings-modal__button link-16" onClick={() => setIsPasswordModalOpen(false)}>
                {translations.myAccount.cancel || 'Cancel'}
              </button>
              <button
                id="reset_password_modal_submit"
                className="settings-modal__button link-16"
                onClick={() => sendPasswordResetRequest(userEmail as string)}
              >
                {translations.myAccountSettings.resetPassword || 'Reset Password'}
              </button>
            </div>
          </div>
        </div>
      </Dialog>
      <Dialog isOpen={isDeleteAccountModalOpen} onClose={() => setIsDeleteAccountModalOpen(false)}>
        <div
          className="settings-modal modal modal--large settings-modal--security settings-modal--delete-account-modal"
          ref={delAccountModalRef}
        >
          <div className="modal__wrapper">
            <div className="settings-modal__header modal__header">
              <h5 className="settings-modal__title">
                {translations.myAccountSettings.deleteAccount || 'Delete Account'}
              </h5>
              <CloseButton onClose={() => setIsDeleteAccountModalOpen(false)} />
            </div>
            <div className="settings-modal__content modal__content settings-modal__content--margin">
              <p>
                {translations.myAccountSettings.contactUsToDeleteYourAccountAt ||
                  'If you want to delete your account, please write to us at'}
                {` `}
                <a
                  href={`mailto:${translations.myAccountSettings.contactEmailDeleteAccount || 'publisher@forbes.cz.'}`}
                >
                  {translations.myAccountSettings.contactEmailDeleteAccount || 'publisher@forbes.cz.'}
                </a>
                .
              </p>
            </div>
            <div className="settings-modal__footer modal__footer">
              <button className="settings-modal__button link-16" onClick={() => setIsDeleteAccountModalOpen(false)}>
                {translations.general.ok || 'OK'}
              </button>
            </div>
          </div>
        </div>
      </Dialog>
    </div>
  );
};

export default SecurityPrivacy;
